import { z } from 'zod';
import { ProjectKeySchema, SegmentSchema } from './common.js';

// ---------------------------------------------------------------------------
// KFML-Todoliste (Issue #14, WP-5/WP-6).
//
// On-demand erzeugte To-do-Liste je Kundensegment: größtenteils deterministisch
// (PL/FBL/QM aus dem strukturierten Intake-Block), KI nur für den Freitext
// (`sonstiges`). Diese Schemas beschreiben die Response-Form der Route
// POST /v1/kfml-todos und werden vom Service an der Boundary validiert.
// ---------------------------------------------------------------------------

// Herkunft eines To-do-Items: deterministisch aus strukturierten Feldern oder
// per KI aus dem Freitext extrahiert.
export const KfmlTodoSourceSchema = z.enum(['deterministic', 'ki']);
export type KfmlTodoSource = z.infer<typeof KfmlTodoSourceSchema>;

// Best-Effort-Abgleich des Wunsch-Namens (Intake) gegen den aktuellen SF-Wert.
// Rein heuristische Markierung — `null`, wenn kein SF-Wert zum Vergleich vorliegt
// oder das Item keinen Namensabgleich kennt (z. B. QM, KI-Items).
export const KfmlNameMatchSchema = z.enum(['scheint identisch', 'abweichend']);
export type KfmlNameMatch = z.infer<typeof KfmlNameMatchSchema>;

export const KfmlTodoItemSchema = z.object({
  source: KfmlTodoSourceSchema,
  // Menschenlesbarer To-do-Text (z. B. „PL in SF setzen: Max Mustermann").
  text: z.string(),
  // Aktueller SF-Wert (aufgelöster User-Name), falls für dieses Item relevant.
  current_sf_value: z.string().nullable(),
  // Heuristische Markierung Wunsch ↔ SF-Wert; null wenn nicht anwendbar.
  match: KfmlNameMatchSchema.nullable(),
});
export type KfmlTodoItem = z.infer<typeof KfmlTodoItemSchema>;

export const KfmlProjectTodosSchema = z.object({
  project_key: ProjectKeySchema,
  customer_name: z.string(),
  // SF-Deeplink des führenden Auftrags; kann fehlen (kein SF-Objekt).
  sf_url: z.string().nullable(),
  todos: z.array(KfmlTodoItemSchema),
});
export type KfmlProjectTodos = z.infer<typeof KfmlProjectTodosSchema>;

export const KfmlTodoListSchema = z.object({
  segment: SegmentSchema,
  projects: z.array(KfmlProjectTodosSchema),
  // Markdown-Repräsentation der gesamten Liste (Kopieren / Download in der UI).
  markdown: z.string(),
});
export type KfmlTodoList = z.infer<typeof KfmlTodoListSchema>;

// Request-Body der Route POST /v1/kfml-todos.
export const KfmlTodoRequestSchema = z.object({
  segment: SegmentSchema,
});
export type KfmlTodoRequest = z.infer<typeof KfmlTodoRequestSchema>;
